import { supabase } from '@/lib/supabase/client';
import type { VaultEntryMatchScore } from '@/lib/contact-vault/match';
import type { WeekQueueRankingLead, WeekQueueVaultMatch } from '@/lib/outreach/week-queue-ranking';
import {
  classifyContactReviewBucket,
  getLeadContactTier,
  type ContactReviewBucket,
} from '@/lib/contact-vault/lead-contact-readiness';
import type { ContactVaultEntry, ContactVaultMatchSuggestion, ContactVaultSiblingLead } from '@/types/contact-vault';
import type { Lead, LeadUpdate } from '@/types/leads';
import { leadsService } from '@/lib/services/supabase/leads';
import { assertCanonicalReviewCommandsEnabled } from '@/lib/outreach/activity-release';

export interface VaultStateCoverage {
  state: string | null;
  count: number;
}

export interface LeadContactReviewRow {
  lead: Lead;
  current_tier: ReturnType<typeof getLeadContactTier>;
  best_match: VaultEntryMatchScore | null;
  bucket: ContactReviewBucket;
}

export function vaultContactPayload(entry: ContactVaultEntry): LeadUpdate {
  return {
    contact_person: entry.contact_person ?? null,
    contact_title: entry.title ?? null,
    email: entry.email ?? null,
    phone: entry.phone ?? null,
    contact_resolved_at: new Date().toISOString(),
    contact_resolved_by: null,
  };
}

type RedactedVaultSuggestionRow = {
  lead_id?: string;
  entry_id: string;
  email_tier: ContactVaultEntry['email_tier'];
  title: string | null;
  account_name: string | null;
  location_name: string | null;
  company_name: string | null;
  city: string | null;
  state: string | null;
  quality_score: number | string | null;
  confidence: ContactVaultMatchSuggestion['confidence'];
  match_level: ContactVaultMatchSuggestion['match_level'];
  match_reason: string;
  sibling_leads: ContactVaultSiblingLead[] | null;
  matches_current_contact: boolean | null;
  can_apply: boolean | null;
  review_bucket: ContactReviewBucket | null;
};

function toRedactedEntry(row: RedactedVaultSuggestionRow): ContactVaultEntry {
  return {
    id: row.entry_id,
    email: null,
    email_norm: null,
    email_tier: row.email_tier,
    first_name: null,
    last_name: null,
    contact_person: null,
    phone: null,
    title: row.title,
    account_name: row.account_name,
    account_name_norm: null,
    location_name: row.location_name,
    location_name_norm: null,
    company_name: row.company_name,
    company_name_norm: null,
    city: row.city,
    state: row.state,
    source_tags: [],
    quality_score: Number(row.quality_score ?? 0),
    row_version: 0,
    created_at: '',
    updated_at: '',
  };
}

function toMatchScore(row: RedactedVaultSuggestionRow): VaultEntryMatchScore {
  return {
    entry: toRedactedEntry(row),
    confidence: row.confidence,
    match_level: row.match_level,
    match_reason: row.match_reason,
    chain_key: null,
    score: 0,
  };
}

function buildContactReviewRow(lead: Lead, row?: RedactedVaultSuggestionRow): LeadContactReviewRow {
  const best_match = row ? toMatchScore(row) : null;
  return {
    lead,
    current_tier: getLeadContactTier(lead),
    best_match,
    bucket: row?.review_bucket ?? classifyContactReviewBucket(lead, best_match),
  };
}

async function getSuggestionsForLeadIds(leadIds: string[]): Promise<Map<string, RedactedVaultSuggestionRow[]>> {
  const uniqueLeadIds = [...new Set(leadIds.filter(Boolean))];
  if (uniqueLeadIds.length === 0) return new Map();
  const { data, error } = await supabase.rpc('list_contact_vault_suggestions_for_leads_redacted', {
    p_lead_ids: uniqueLeadIds,
  });
  if (error) throw new Error(`Failed to load vault suggestions: ${error.message}`);
  const grouped = new Map<string, RedactedVaultSuggestionRow[]>();
  for (const row of ((data as RedactedVaultSuggestionRow[] | null) ?? [])) {
    if (!row.lead_id) continue;
    const entries = grouped.get(row.lead_id) ?? [];
    entries.push(row);
    grouped.set(row.lead_id, entries);
  }
  return grouped;
}

export const contactVaultService = {
  async getVaultEntryCount(): Promise<number> {
    const { data, error } = await supabase.rpc('get_contact_vault_admin_summary');

    if (error) {
      console.warn('Unable to count contact vault entries:', error.message);
      return 0;
    }

    const summary = Array.isArray(data) ? data[0] : data;
    return Number((summary as { entry_count?: number | string } | null)?.entry_count ?? 0);
  },

  async getStateCoverage(): Promise<VaultStateCoverage[]> {
    const { data, error } = await supabase.rpc('get_contact_vault_admin_summary');
    if (error) {
      console.warn('Unable to load vault state coverage:', error.message);
      return [];
    }

    const summary = (Array.isArray(data) ? data[0] : data) as { state_counts?: Record<string, number | string> } | null;
    return Object.entries(summary?.state_counts ?? {})
      .map(([state, count]) => ({ state: state === 'unknown' ? null : state.toUpperCase(), count: Number(count) }))
      .sort((left, right) => right.count - left.count);
  },

  async buildContactReviewForLeads(leads: Lead[]): Promise<LeadContactReviewRow[]> {
    if (leads.length === 0) return [];
    const suggestionsByLeadId = await getSuggestionsForLeadIds(leads.map((lead) => lead.id));
    return leads.map((lead) => buildContactReviewRow(lead, suggestionsByLeadId.get(lead.id)?.[0]));
  },

  async buildVaultMatchesForRankingLeads(
    leads: WeekQueueRankingLead[]
  ): Promise<Map<string, WeekQueueVaultMatch | null>> {
    void leads;
    return new Map();
  },

  async applyContactFromVault(leadId: string, entry: ContactVaultEntry, siblingIds: string[] = []): Promise<Lead> {
    const [updatedLead] = await this.applyContactToLeads([leadId, ...siblingIds], entry);
    if (!updatedLead) throw new Error('Lead contact update returned no row.');
    return updatedLead;
  },

  async applyContactToLeads(leadIds: string[], entry: ContactVaultEntry): Promise<Lead[]> {
    const uniqueLeadIds = [...new Set(leadIds.filter(Boolean))];
    if (uniqueLeadIds.length === 0) return [];
    assertCanonicalReviewCommandsEnabled();
    const { data, error } = await supabase.rpc('apply_contact_vault_entry_to_leads_redacted', {
      p_lead_ids: uniqueLeadIds, p_entry_id: entry.id,
    });
    if (error) throw new Error(`Failed to apply vault contact: ${error.message}`);
    return ((data as Record<string, unknown>[] | null) ?? []).map((row) => row as unknown as Lead);
  },

  async resolveContact(leadId: string): Promise<Lead> {
    const { error } = await supabase.rpc('update_leads_redacted', {
      p_lead_ids: [leadId],
      p_changes: { contact_resolved_at: new Date().toISOString(), contact_resolved_by: null },
    });
    if (error) throw new Error(`Failed to resolve contact: ${error.message}`);
    const updatedLead = await leadsService.getById(leadId);
    if (!updatedLead) throw new Error('Lead contact update returned no row.');
    return updatedLead;
  },

  /**
   * Clear named contacts that do not align with the store name (corporate/chain fan-out).
   * Leaves the lead open for a better store-level vault apply.
   */
  async clearMismatchedAppliedContacts(leadIds: string[]): Promise<{ cleared: number; skipped: number }> {
    void leadIds;
    throw new Error('Vault-contact cleanup is disabled pending provenance and versioned review controls.');
  },

  async getSuggestionsForLead(lead: Lead): Promise<ContactVaultMatchSuggestion[]> {
    const suggestionsByLeadId = await getSuggestionsForLeadIds([lead.id]);
    return (suggestionsByLeadId.get(lead.id) ?? []).map((row) => ({
      entry: toRedactedEntry(row),
      confidence: row.confidence,
      match_level: row.match_level,
      match_reason: row.match_reason,
      sibling_leads: Array.isArray(row.sibling_leads) ? row.sibling_leads : [],
      matches_current_contact: row.matches_current_contact === true,
      can_apply: row.can_apply === true,
    }));
  },
};
